import { useEffect, useRef, useState } from 'react';
import type { AiConnection, AiConnectionId, AiOverview, AiProviderId, Settings } from '@momomail/shared';
import { api, bridge, errorText } from '../lib/bridge';
import { t, tMessage } from '../lib/i18n';
import { Icon } from './Icon';
import { Row, Switch } from './Settings';
import { labelTone } from './MessageList';

/**
 * Settings → AI: choose which AI to use. One card per provider; click a card to select it;
 * unconnected cards are grayed out — first "网页登录" (web sign-in, uses an existing plan) or "输入 API Key" (enter API key).
 * Sign-in happens on the server: this shows the authorization URL and code (for Claude, paste back the code from the authorization page). Each user's sign-ins are independent.
 */

const PROVIDERS: { id: AiProviderId; name: string; console: string; guide: string }[] = [
  { id: 'claude', name: 'Claude', console: 'https://platform.claude.com/settings/keys', guide: 'https://platform.claude.com/docs/en/get-api-key' },
  { id: 'codex', name: 'Codex / OpenAI', console: 'https://platform.openai.com/api-keys', guide: 'https://help.openai.com/en/articles/4936850-where-do-i-find-my-openai-api-key' },
  { id: 'kimi', name: 'Kimi', console: 'https://platform.kimi.com/console/api-keys', guide: 'https://platform.kimi.com/docs/get-api-key' },
  { id: 'qwen', name: 'Qwen', console: 'https://bailian.console.aliyun.com/cn-beijing/model/settings/api-key', guide: 'https://help.aliyun.com/zh/model-studio/get-api-key/' },
  { id: 'deepseek', name: 'DeepSeek', console: 'https://platform.deepseek.com/api_keys', guide: 'https://api-docs.deepseek.com/zh-cn/' },
];

/** Where the backend is: "本机" (this computer) in local mode (backend bundled in the desktop app), "服务器上" (on the server) when connected to a server. */
const where = () => (bridge.connection.info().mode === 'local' ? t('本机') : t('服务器上'));
/** Ready to use: signed in / has a key (local connection: already selected once). */
const ready = (c: AiConnection) => (c.method === 'api' ? c.configured : c.loggedIn);
/** Selectable: ready to use, or an existing sign-in / key was detected on the system (click to use). */
const usableNow = (c: AiConnection) => ready(c) || c.localAvailable;
function statusText(c: AiConnection) {
  if (!c.local) return c.method === 'login' ? t('已网页登录（套餐）') : t('已保存 API Key');
  if (c.method === 'login') return ready(c) ? t('用{where}已登录的账号', { where: where() }) : t('检测到{where}已登录', { where: where() });
  return c.localAvailable ? t('检测到{where}的 API Key（{source}）', { where: where(), source: c.localSource ?? '' }) : t('已保存{where}的 API Key', { where: where() });
}
const sourceLabel = (c: AiConnection) => (c.local ? (c.method === 'login' ? t('{where}登录', { where: where() }) : t('{where} API Key', { where: where() })) : c.method === 'login' ? t('网页登录') : t('填写的 API Key'));

export function AiSettings({ settings, update }: { settings: Settings; update: (patch: Partial<Settings>) => Promise<void> }) {
  const [overview, setOverview] = useState<AiOverview | null>(null);
  const [error, setError] = useState('');
  const [errorProvider, setErrorProvider] = useState('');
  const [pending, setPending] = useState(false);
  const [formId, setFormId] = useState<AiConnectionId | null>(null);
  const [key, setKey] = useState('');
  const [model, setModel] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [code, setCode] = useState('');
  const [prompt, setPrompt] = useState('');
  const [answer, setAnswer] = useState<{ text: string; ms: number } | null>(null);
  const [asking, setAsking] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    let polling = false;
    const refresh = async () => {
      if (polling) return;
      polling = true;
      try {
        const o = await api().ai.overview();
        if (alive.current) setOverview(o);
      } catch (e) {
        if (alive.current) setError(errorText(e));
      } finally {
        polling = false;
      }
    };
    void refresh();
    // Sign-in happens on the server: poll for the authorization URL, code and result
    const timer = setInterval(() => void refresh(), 1000);
    return () => {
      alive.current = false;
      clearInterval(timer);
    };
  }, []);

  const status = overview?.status;
  const busy = pending || status?.phase === 'logging-in' || status?.phase === 'verifying';

  const act = async (provider: string, fn: () => Promise<AiOverview>) => {
    setPending(true);
    setError('');
    setErrorProvider(provider);
    try {
      setOverview(await fn());
      return true;
    } catch (e) {
      setError(errorText(e));
      return false;
    } finally {
      setPending(false);
    }
  };

  const edit = (c: AiConnection) => {
    setFormId(c.id);
    setKey('');
    setModel(c.model ?? '');
    setBaseUrl(c.baseUrl ?? c.baseUrls[0] ?? '');
    setError('');
  };

  const save = async (c: AiConnection) => {
    if (!key.trim()) return;
    const ok = await act(c.provider, () => api().ai.configure({ id: c.id, apiKey: key.trim(), ...(model ? { model } : {}), ...(baseUrl ? { baseUrl } : {}) }));
    if (ok) {
      setFormId(null);
      setKey('');
    }
  };

  const ask = async () => {
    if (!prompt.trim()) return;
    setAsking(true);
    setAnswer(null);
    setError('');
    setErrorProvider('');
    try {
      const r = await api().ai.run(prompt.trim());
      setAnswer({ text: r.text, ms: r.durationMs });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setAsking(false);
    }
  };

  if (!overview) {
    return (
      <div className="settings__section">
        <h3 className="settings__h">AI</h3>
        {error ? <div className="notice notice--error">{error}</div> : <span className="spinner" />}
      </div>
    );
  }

  const { connections } = overview;
  const selectedId = status?.ready ? status.connectionId : null;
  // Usable ones first
  const providers = [...PROVIDERS].sort((a, b) => Number(connections.some((c) => c.provider === b.id && ready(c))) - Number(connections.some((c) => c.provider === a.id && ready(c))));

  return (
    <div className="settings__section ai-picker">
      <h3 className="settings__h">AI</h3>
      <p className="muted settings__intro">
        {t('选一个 AI 给馍馍邮箱用（以后的分类、摘要、起草回复、日历都用它）。点卡片选用；灰色卡片要先网页登录（用你已有的套餐）或填 API Key。登录和 Key 只属于你自己，别的用户看不到。')}
      </p>

      <div className="ai-picker__cards" role="radiogroup" aria-label={t('用哪个 AI')}>
        {providers.map((provider) => {
          const mine = connections.filter((c) => c.provider === provider.id);
          // Separate in-app ones (signed in / key entered here) from local ones (already on the system)
          const apiConn = mine.find((c) => c.method === 'api' && !c.local);
          const loginConn = mine.find((c) => c.method === 'login' && !c.local);
          const locals = mine.filter((c) => c.local);
          const usable = mine.filter(usableNow);
          const preferred = usable.find((c) => c.id === status?.connectionId) ?? usable.find((c) => ready(c) && c.method === 'login') ?? usable.find(ready) ?? usable.find((c) => c.method === 'login') ?? usable[0];
          const selected = !!selectedId && mine.some((c) => c.id === selectedId);
          const available = mine.some((c) => c.available);
          const loggingIn = !!loginConn && status?.loginFor === loginConn.id && status.phase === 'logging-in';
          const hasLocal = locals.some(usableNow);
          const label = loggingIn ? t('正在登录…') : preferred ? statusText(preferred) : !available ? t('{where}没有装这个 AI', { where: where() }) : loginConn ? t('未连接 · 先网页登录或填写 API Key') : t('未连接 · 先填写 API Key');
          const editing = !!apiConn && formId === apiConn.id;
          // Local connection: selecting it means consenting to use it (re-reads the latest from the system each time); others are selected directly
          const pick = (c: AiConnection) => void act(provider.id, () => (c.local && c.localAvailable ? api().ai.useLocal(c.id) : api().ai.select(c.id)));
          const choose = () => preferred && !busy && pick(preferred);
          return (
            <article
              key={provider.id}
              className={`ai-card${selected ? ' is-selected' : ''}${!usable.length ? ' is-unavailable' : ''}`}
              aria-label={provider.name}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('button,a,input,select,label,details,form,textarea')) return;
                choose();
              }}
            >
              <button type="button" className="ai-card__select" role="radio" aria-checked={selected} aria-label={t('选择 {name}', { name: provider.name })} disabled={busy || !usable.length} onClick={choose}>
                <span className="ai-card__radio" aria-hidden>
                  {selected && <i />}
                </span>
                <img className="ai-card__logo" src={`ai-logos/${provider.id}.svg`} alt="" aria-hidden />
                <span className="ai-card__identity">
                  <span className="ai-card__name">{provider.name}</span>
                  <span className="ai-card__state">{label}</span>
                </span>
                {selected && <span className="ai-card__badge">{t('已选择')}</span>}
              </button>

              {usable.length > 1 && (
                <div className="ai-card__sources">
                  <span className="muted">{t('用哪种：')}</span>
                  {usable.map((c) => (
                    <button key={c.id} className="btn btn--small btn--quiet" aria-pressed={selectedId === c.id} disabled={busy} onClick={() => pick(c)}>
                      {sourceLabel(c)}
                    </button>
                  ))}
                </div>
              )}

              <div className="ai-card__actions">
                {loginConn && !loginConn.loggedIn && !loggingIn && (
                  <button className="btn btn--small btn--quiet" disabled={busy || !loginConn.available} onClick={() => void act(provider.id, () => api().ai.login(loginConn.id))}>
                    {hasLocal ? t('另外网页登录一个账号') : t('网页登录')}
                  </button>
                )}
                {apiConn && (
                  <button className="btn btn--small btn--quiet" disabled={busy || !apiConn.available} onClick={() => edit(apiConn)}>
                    {apiConn.configured ? t('换一个 API Key') : t('输入 API Key')}
                  </button>
                )}
                {loginConn?.loggedIn && (
                  <button className="btn btn--small btn--quiet" disabled={busy} onClick={() => void act(provider.id, () => api().ai.logout(loginConn.id))}>
                    {t('登出')}
                  </button>
                )}
                {apiConn?.configured && (
                  <button className="btn btn--small btn--quiet" disabled={busy} onClick={() => void act(provider.id, () => api().ai.logout(apiConn.id))}>
                    {t('删除 API Key')}
                  </button>
                )}
                {locals.filter(ready).map((c) => (
                  <button key={c.id} className="btn btn--small btn--quiet" disabled={busy} title={t('只是这里不再使用，{where}系统里的登录 / Key 不受影响', { where: where() })} onClick={() => void act(provider.id, () => api().ai.logout(c.id))}>
                    {t('停止使用{source}', { source: sourceLabel(c) })}
                  </button>
                ))}
                <a href={provider.console} target="_blank" rel="noopener noreferrer">
                  {t('获取 API Key ↗')}
                </a>
                <a href={provider.guide} target="_blank" rel="noopener noreferrer">
                  {t('如何获取？↗')}
                </a>
              </div>

              {selected && preferred && preferred.models.length > 0 && provider.id !== 'kimi' && (
                <label className="ai-card__model">
                  <span className="muted">{t('模型')}</span>
                  <select
                    className="setting__select"
                    value={preferred.model ?? ''}
                    disabled={busy}
                    onChange={(e) => void act(provider.id, () => api().ai.configure({ id: preferred.id, model: e.target.value }))}
                  >
                    {[...new Map([...preferred.models.map((m) => [m.id, m.label] as const), ...(preferred.model && !preferred.models.some((m) => m.id === preferred.model) ? [[preferred.model, preferred.model] as const] : [])])].map(([id, name]) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {preferred?.local && preferred.localAvailable && !ready(preferred) && (
                <p className="ai-card__hint">
                  {t('选择这张卡片，就是同意使用{where}已有的凭证（{source}）。会复制一份给你用，原来的登录不受影响。', { where: where(), source: preferred.localSource ?? '' })}
                </p>
              )}
              {!usable.length && provider.id === 'kimi' && <p className="ai-card__hint">{t('网页登录需要 Kimi Code 套餐（Plus 或以上）。')}</p>}

              {editing && apiConn && (
                <form
                  className="ai-card__form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void save(apiConn);
                  }}
                >
                  <p className="ai-card__hint">{t('打开"获取 API Key"，创建一个 Key，复制后粘贴到这里。保存前会先用它连一次，确认能用。')}</p>
                  <label className="field">
                    <span className="field__label">API Key</span>
                    <input autoFocus type="password" value={key} autoComplete="off" disabled={busy} placeholder={t('粘贴 API Key')} onChange={(e) => setKey(e.target.value)} />
                  </label>
                  <details className="ai-card__advanced">
                    <summary>{t('高级设置')}</summary>
                    <label className="field">
                      <span className="field__label">{t('模型')}</span>
                      <input value={model} disabled={busy} list={`models-${apiConn.id}`} onChange={(e) => setModel(e.target.value)} />
                      <datalist id={`models-${apiConn.id}`}>
                        {apiConn.models.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.label}
                          </option>
                        ))}
                      </datalist>
                    </label>
                    {apiConn.baseUrls.length > 1 && (
                      <label className="field">
                        <span className="field__label">{t('官方 API 地址')}</span>
                        <select className="setting__select" value={baseUrl} disabled={busy} onChange={(e) => setBaseUrl(e.target.value)}>
                          {apiConn.baseUrls.map((u) => (
                            <option key={u}>{u}</option>
                          ))}
                        </select>
                      </label>
                    )}
                  </details>
                  <div className="ai-card__actions">
                    <button type="submit" className="btn btn--small btn--primary" disabled={busy || !key.trim()}>
                      {pending ? t('正在验证…') : t('保存并选择')}
                    </button>
                    <button type="button" className="btn btn--small btn--quiet" disabled={pending} onClick={() => setFormId(null)}>
                      {t('取消')}
                    </button>
                  </div>
                </form>
              )}

              {loggingIn && status && (
                <div className="ai-card__login" role="status">
                  {!status.url ? (
                    <p>
                      <span className="spinner" /> {t('正在准备登录…')}
                    </p>
                  ) : (
                    <>
                      <p>
                        {t('1. 打开授权网页，用你的 {name} 账号登录并同意。', { name: provider.name })}
                        {status.code && (
                          <>
                            {' '}
                            {t('页面要求输入授权码时填：')}
                            <b className="ai-card__code">{status.code}</b>
                            <button className="bare ai-card__copy" title={t('复制授权码')} onClick={() => void navigator.clipboard?.writeText(status.code)}>
                              <Icon name="copy" size={14} />
                            </button>
                          </>
                        )}
                      </p>
                      <div className="ai-card__actions">
                        <a className="btn btn--small btn--primary" href={status.url} target="_blank" rel="noopener noreferrer">
                          {t('打开授权网页 ↗')}
                        </a>
                      </div>
                      {status.needsCode ? (
                        <form
                          className="ai-card__paste"
                          onSubmit={(e) => {
                            e.preventDefault();
                            if (code.trim()) void act(provider.id, () => api().ai.submitCode(code.trim())).then((ok) => ok && setCode(''));
                          }}
                        >
                          <label className="field">
                            <span className="field__label">{t('2. 授权后页面上会显示一段代码，复制贴到这里')}</span>
                            <input value={code} autoComplete="off" placeholder={t('粘贴代码')} onChange={(e) => setCode(e.target.value)} />
                          </label>
                          <button type="submit" className="btn btn--small" disabled={pending || !code.trim()}>
                            {t('提交')}
                          </button>
                        </form>
                      ) : (
                        <p className="muted">
                          <span className="spinner" /> {status.code ? t('2. 输入授权码并同意后，这里会自动完成。') : t('等你在网页上完成授权…')}
                        </p>
                      )}
                    </>
                  )}
                  <button className="btn btn--small btn--quiet" onClick={() => void act(provider.id, () => api().ai.cancelLogin())}>
                    {t('取消登录')}
                  </button>
                </div>
              )}

              {status?.phase === 'error' && status.error && mine.some((c) => c.id === status.connectionId || c.id === status.loginFor) && !pending && <p className="ai-card__error" role="alert">{tMessage(status.error)}</p>}
              {error && errorProvider === provider.id && <p className="ai-card__error" role="alert">{error}</p>}
            </article>
          );
        })}
      </div>

      <TaggingSettings overview={overview} busy={busy} settings={settings} update={update} onAssign={(fn) => act('', fn)} />
      <UnsubscribeSettings overview={overview} busy={busy} onAssign={(fn) => act('', fn)} />
      <AssistantSettings overview={overview} busy={busy} onAssign={(fn) => act('', fn)} />

      {status?.ready && (
        <div className="ai-try">
          <h3 className="settings__h">{t('试一试')}</h3>
          <p className="muted settings__intro">{t('AI 在服务器上运行，只回答文字：不能执行命令、不能读写文件。')}</p>
          <form
            className="ai-try__form"
            onSubmit={(e) => {
              e.preventDefault();
              void ask();
            }}
          >
            <textarea value={prompt} rows={3} placeholder={t('比如：用一句话介绍你自己')} disabled={asking} onChange={(e) => setPrompt(e.target.value)} />
            <button type="submit" className="btn btn--small btn--primary" disabled={asking || !prompt.trim()}>
              {asking ? (
                <>
                  <span className="spinner" /> {t('正在回答…')}
                </>
              ) : (
                t('发送')
              )}
            </button>
          </form>
          {answer && (
            <div className="ai-try__answer">
              <p>{answer.text}</p>
              <span className="muted">{t('用时 {s} 秒', { s: (answer.ms / 1000).toFixed(1) })}</span>
            </div>
          )}
          {error && !errorProvider && <p className="ai-card__error" role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * "邮件标签" (AI tags): which AI (and model) tags new inbox mail; off when none is picked. Only connections that are signed in /
 * have a key can be picked. Also: move ads and spam to the spam folder, and how the tagging is going.
 */
function TaggingSettings({
  overview,
  busy,
  settings,
  update,
  onAssign,
}: {
  overview: AiOverview;
  busy: boolean;
  settings: Settings;
  update: (patch: Partial<Settings>) => Promise<void>;
  onAssign: (fn: () => Promise<AiOverview>) => Promise<boolean>;
}) {
  const usable = overview.connections.filter(ready);
  const picked = overview.purposes.tagging ?? null;
  const conn = picked ? overview.connections.find((c) => c.id === picked.connectionId) : undefined;
  const name = (c: AiConnection) => `${PROVIDERS.find((p) => p.id === c.provider)?.name ?? c.provider} · ${sourceLabel(c)}`;
  const s = overview.tagging;
  const assign = (connectionId: AiConnectionId | '', model: string | null) => void onAssign(() => api().ai.assign('tagging', connectionId ? { connectionId, model } : null));
  return (
    <div className="ai-purpose">
      <h3 className="settings__h">{t('邮件标签')}</h3>
      <p className="muted settings__intro">
        {t('让 AI 给收件箱的每封新邮件打标签（工作、个人、广告……），并判断要不要马上告诉你：只有值得看的邮件才发通知，紧急的更醒目。第一次打开时会先给最近的 50 封打上。AI 只读邮件，不能做任何操作，也不会记住内容；这件事量大，建议选便宜、快的模型。')}
      </p>
      <Row title={t('用哪个 AI')} {...(usable.length ? {} : { desc: t('先在上面登录一个 AI 或填写 API Key。') })}>
        <select className="setting__select" value={picked?.connectionId ?? ''} disabled={busy || !usable.length} onChange={(e) => assign(e.target.value as AiConnectionId | '', null)}>
          <option value="">{t('不打标签')}</option>
          {usable.map((c) => (
            <option key={c.id} value={c.id}>
              {name(c)}
            </option>
          ))}
        </select>
      </Row>
      {picked && conn && (
        <Row title={t('模型')} desc={t('便宜、快的模型就够用，比如 Claude Haiku、千问 Flash、DeepSeek Flash。')}>
          <select className="setting__select" value={picked.model ?? ''} disabled={busy} onChange={(e) => assign(picked.connectionId, e.target.value || null)}>
            <option value="">{t('和这个 AI 的设置一样（{model}）', { model: conn.model ?? t('默认') })}</option>
            {conn.models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </Row>
      )}
      {picked && (
        <Row title={t('垃圾邮件移到垃圾箱')} desc={t('关着时只打标签。打开后，AI 标为"垃圾"的邮件会移到邮箱的垃圾邮件文件夹（Gmail 等网页上也一样），移错了可以移回来。')}>
          <Switch checked={settings.aiAutoSpam} onChange={(v) => void update({ aiAutoSpam: v })} label={t('垃圾邮件移到垃圾箱')} />
        </Row>
      )}
      {picked && <TagLabels settings={settings} update={update} />}
      {picked && <TagRules settings={settings} update={update} />}
      {picked && (
        <p className="muted ai-purpose__status">
          {t('已打标签 {n} 封（今天 {today} 封）', { n: s.tagged, today: s.today })}
          {s.pending > 0 && <> · {t('还有 {n} 封在排队', { n: s.pending })}</>}
          {s.lastError && <span className="ai-card__error"> · {t('上次出错：{error}', { error: tMessage(s.lastError) })}</span>}{' '}
          <button className="field__link" disabled={busy || s.pending > 0} onClick={() => void onAssign(() => api().ai.retag())}>
            {t('按现在的标签和规则重新打最近 50 封')}
          </button>
        </p>
      )}
    </div>
  );
}

/**
 * The user's own tags: any number can apply to one message, next to the system's importance and needs-reply. Each can say what it's
 * for, which helps the AI. 馍馍 can propose changes too (the user confirms them in the chat).
 */
function TagLabels({ settings, update }: { settings: Settings; update: (patch: Partial<Settings>) => Promise<void> }) {
  const [list, setList] = useState(settings.aiLabels);
  const [name, setName] = useState('');
  // 馍馍 (or another device) changed them: show the new list unless the user has unsaved changes
  const saved = useRef(settings.aiLabels);
  useEffect(() => {
    if (JSON.stringify(list) === JSON.stringify(saved.current)) setList(settings.aiLabels);
    saved.current = settings.aiLabels;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.aiLabels]);
  const dirty = JSON.stringify(list) !== JSON.stringify(settings.aiLabels);
  const add = () => {
    const n = name.trim();
    if (n && !list.some((l) => l.name === n)) setList([...list, { name: n, hint: '' }]);
    setName('');
  };
  return (
    <div className="setting setting--column">
      <div className="setting__text">
        <div className="setting__title">{t('我的标签')}</div>
        <div className="setting__desc muted">{t('一封邮件可以同时有好几个标签，和系统的"重要""待回复"等一起显示。写上标签是干什么的，AI 会分得更准。')}</div>
      </div>
      <div className="ai-labels">
        {list.map((l, i) => (
          <div key={l.name} className="ai-labels__row">
            <span className={`ai-tag ai-tag--c${labelTone(l.name)}`}>{l.name}</span>
            <input
              className="ai-labels__hint"
              value={l.hint}
              maxLength={200}
              placeholder={t('什么样的邮件算这个（可选）')}
              onChange={(e) => setList(list.map((x, k) => (k === i ? { ...x, hint: e.target.value } : x)))}
            />
            <button className="btn btn--icon btn--quiet" title={t('删除标签')} aria-label={t('删除标签')} onClick={() => setList(list.filter((_, k) => k !== i))}>
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
        <form
          className="ai-labels__row"
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <input value={name} maxLength={24} placeholder={t('新标签，比如：学习')} onChange={(e) => setName(e.target.value)} />
          <button type="submit" className="btn btn--small" disabled={!name.trim()}>
            {t('添加')}
          </button>
        </form>
      </div>
      <div className="setting__control">
        <button className="btn btn--small btn--primary" disabled={!dirty} onClick={() => void update({ aiLabels: list })}>
          {t('保存标签')}
        </button>
      </div>
    </div>
  );
}

/**
 * The user's extra instructions for the tagging AI, taking precedence over its own judgment. 馍馍 can propose changes too
 * (the user confirms them in the chat).
 */
function TagRules({ settings, update }: { settings: Settings; update: (patch: Partial<Settings>) => Promise<void> }) {
  const [text, setText] = useState(settings.aiTagRules);
  // 馍馍 (or another device) changed them: show the new rules unless the user is mid-edit
  const saved = useRef(settings.aiTagRules);
  useEffect(() => {
    if (text === saved.current) setText(settings.aiTagRules);
    saved.current = settings.aiTagRules;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.aiTagRules]);
  return (
    <div className="setting setting--column">
      <div className="setting__text">
        <div className="setting__title">{t('给打标签 AI 的额外说明')}</div>
        <div className="setting__desc muted">{t('优先于它自己的判断。也可以让馍馍帮你改。')}</div>
      </div>
      <textarea
        className="ai-rules"
        rows={5}
        maxLength={4000}
        value={text}
        placeholder={[t('比如：'), t('GitHub 上 momo/mail 仓库的 PR 通知都算垃圾'), t('导师 zhang@school.edu 的邮件一律重要'), t('学校教务处的邮件都打上"学习"')].join('\n')}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="setting__control">
        <button className="btn btn--small btn--primary" disabled={text === settings.aiTagRules} onClick={() => void update({ aiTagRules: text })}>
          {t('保存规则')}
        </button>
      </div>
    </div>
  );
}

/** Which AI is 馍馍, the assistant you chat with (tap the cat): unset = the selected AI. */
function AssistantSettings({ overview, busy, onAssign }: { overview: AiOverview; busy: boolean; onAssign: (fn: () => Promise<AiOverview>) => Promise<boolean> }) {
  const usable = overview.connections.filter(ready);
  const picked = overview.purposes.assistant ?? null;
  const conn = picked ? overview.connections.find((c) => c.id === picked.connectionId) : undefined;
  const name = (c: AiConnection) => `${PROVIDERS.find((p) => p.id === c.provider)?.name ?? c.provider} · ${sourceLabel(c)}`;
  if (!usable.length) return null;
  const assign = (connectionId: string, model: string | null) => void onAssign(() => api().ai.assign('assistant', connectionId ? { connectionId: connectionId as AiConnectionId, model } : null));
  return (
    <div className="ai-purpose">
      <h3 className="settings__h">{t('馍馍助理')}</h3>
      <p className="muted settings__intro">{t('点右下角的馍馍就能和它聊天：找邮件、写回复、整理收件箱、安排日程、改设置。它在服务器上运行，只能用馍馍邮箱给它的工具；发邮件、删日程等会先问你。建议选聪明一点的模型。')}</p>
      <Row title={t('用哪个 AI')}>
        <select className="setting__select" value={picked?.connectionId ?? ''} disabled={busy} onChange={(e) => assign(e.target.value, null)}>
          <option value="">{t('和上面选中的一样')}</option>
          {usable.map((c) => (
            <option key={c.id} value={c.id}>
              {name(c)}
            </option>
          ))}
        </select>
      </Row>
      {picked && conn && (
        <Row title={t('模型')}>
          <select className="setting__select" value={picked.model ?? ''} disabled={busy} onChange={(e) => assign(picked.connectionId, e.target.value || null)}>
            <option value="">{t('和这个 AI 的设置一样（{model}）', { model: conn.model ?? t('默认') })}</option>
            {conn.models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </Row>
      )}
    </div>
  );
}

/** Which AI works unsubscribe web pages (when the user taps 退订): unset = the tagging AI, then the selected one. */
function UnsubscribeSettings({ overview, busy, onAssign }: { overview: AiOverview; busy: boolean; onAssign: (fn: () => Promise<AiOverview>) => Promise<boolean> }) {
  const usable = overview.connections.filter(ready);
  const picked = overview.purposes.unsubscribe ?? null;
  const name = (c: AiConnection) => `${PROVIDERS.find((p) => p.id === c.provider)?.name ?? c.provider} · ${sourceLabel(c)}`;
  if (!usable.length) return null;
  return (
    <div className="ai-purpose">
      <h3 className="settings__h">{t('退订')}</h3>
      <Row title={t('退订网页用哪个 AI')} desc={t('点邮件里的"退订"时，需要在网页上操作的由 AI 在服务器上完成：它只能看网页、点链接、填表，不会登录或填写密码。')}>
        <select
          className="setting__select"
          value={picked?.connectionId ?? ''}
          disabled={busy}
          onChange={(e) => void onAssign(() => api().ai.assign('unsubscribe', e.target.value ? { connectionId: e.target.value as AiConnectionId, model: null } : null))}
        >
          <option value="">{t('和邮件标签用同一个')}</option>
          {usable.map((c) => (
            <option key={c.id} value={c.id}>
              {name(c)}
            </option>
          ))}
        </select>
      </Row>
    </div>
  );
}
